// Units left per aisle — the data behind the "Units left by aisle"
// popup on the picking screen. A picker blocked mid-aisle (a joey, a
// pallet, another picker) wants to know what is still owed WHERE before
// they decide whether to wait, push past, or skip to another aisle and
// come back — or tap a location in the popup to pick from there next.
// The popup is deliberately plain (picker feedback via Alex, 2026-10-13:
// an earlier tunnel-side split was "over-complicated"): every location
// still to pick with its quantity, grouped under the aisle pair it
// belongs to, with the pair's total.
//
// Pure — ActiveWaveScreen feeds it the same live line set the rest of
// the hero derives from, so the numbers move on every accepted pick
// (and on a skip-location re-anchor, missing-flow write-off, or Large
// JIT order append, all of which land in that set).
//
// Floor layout (Alex, 2026-10-13). Pembroke's racking is walked as
// letter PAIRS — one walkway serves A and B, the next C and D, and so
// on to Y and Z; AA stands alone. The zone names on the floor say the
// same thing ("LOW K-L", "LOW Y-Z", "LOW AA"). Other warehouses have no
// recorded pairing — they get one group per aisle.
import type { ActiveWaveLine } from "../../hooks/useActiveWave";
import { stripWarehouse } from "./locationDisplay";

export interface AisleLocation {
    /** The raw location barcode — stable row key. */
    location: string;
    /** What the picker reads on the rack: "K-12-L3". */
    display: string;
    /** Units still to pick here across every open line. Always > 0. */
    remaining: number;
    /** The picker's current line is at this location. */
    current: boolean;
}

export interface AisleGroup {
    /** Stable id for the group: "K-L", "AA", "1". */
    key: string;
    /** As shown to the picker: "K–L", "AA". */
    label: string;
    /** Units still to pick in the group: Σ over its locations. */
    remaining: number;
    /** The picker's current line is in this group. */
    current: boolean;
    /** Every location in the group with units left, in walk order. */
    locations: AisleLocation[];
}

export interface AisleLayout {
    /** Which group an aisle belongs to. */
    groupOf: (aisle: string) => { key: string; label: string };
}

const isSingleLetter = (aisle: string): boolean => /^[A-Z]$/.test(aisle);

// A ↔ B, C ↔ D … Y ↔ Z: the pair is the letter's even/odd neighbour.
const letterPair = (aisle: string): { key: string; label: string } => {
    if (!isSingleLetter(aisle)) return { key: aisle, label: aisle };
    const i = aisle.charCodeAt(0) - 65;
    const first = String.fromCharCode(65 + (i - (i % 2)));
    const second = String.fromCharCode(65 + (i - (i % 2)) + 1);
    return { key: `${first}-${second}`, label: `${first}–${second}` };
};

export const PEMBROKE_LAYOUT: AisleLayout = { groupOf: letterPair };

export const PLAIN_LAYOUT: AisleLayout = { groupOf: aisle => ({ key: aisle, label: aisle }) };

export const aisleLayoutFor = (warehouse: string | null | undefined): AisleLayout =>
    warehouse === "Pembroke" ? PEMBROKE_LAYOUT : PLAIN_LAYOUT;

// Only lines the picker can still act on count. Picked is done;
// missing / flagged units have been written off by the missing flow and
// will not be found by walking back.
const OPEN_STATUSES = new Set<ActiveWaveLine["status"]>(["pending", "picking"]);

type LocationBits = Pick<ActiveWaveLine, "aisle" | "location" | "location_display">;

const displayOf = (line: LocationBits): string =>
    stripWarehouse(line.location_display ?? line.location ?? "");

/**
 * The aisle a line sits in. Prefers the BE's `aisle` column (locations
 * join); falls back to the first segment of the aisle-bay-level display
 * string, with the warehouse prefix stripped so "NP-A-1-1" reads as
 * aisle "A", not "NP". A location with no dashes at all (a lane, an
 * area, an unjoined barcode) is its own "aisle" — better a true label
 * than a bucket called "other".
 */
export const aisleOfLine = (line: LocationBits): string => {
    const fromJoin = line.aisle != null ? String(line.aisle).trim() : "";
    if (fromJoin) return fromJoin.toUpperCase();
    const display = displayOf(line);
    const head = display.split("-")[0]?.trim();
    return (head || display || "?").toUpperCase();
};

const remainingOf = (line: ActiveWaveLine): number =>
    Math.max(0, Number(line.qty_required) - Number(line.qty_picked));

// The wave's pick order. sort_index is what the BE advances through
// (and renumbers on a skip-to-location, pushing bypassed lines to the
// end); walk_order is the static floor position and only a fallback.
const walkOf = (line: ActiveWaveLine): number => {
    const sort = Number(line.sort_index);
    if (Number.isFinite(sort)) return sort;
    const walk = Number(line.walk_order);
    return Number.isFinite(walk) ? walk : Number.MAX_SAFE_INTEGER;
};

interface LocSlot { display: string; remaining: number; firstWalk: number }
interface GroupSlot { label: string; remaining: number; firstWalk: number; locs: Map<string, LocSlot> }

/**
 * Every aisle group that still has units, in pick order (the lowest
 * sort_index among the group's open lines decides its slot, so the
 * current group is first and skipped-and-deferred groups sit at the
 * end, where the BE will actually bring the picker back to them). Each
 * group lists its locations the same way, several lines at one
 * location folded into one row.
 */
export const aisleUnitsFor = (
    lines: ActiveWaveLine[],
    currentLine: ActiveWaveLine | null,
    warehouse?: string | null,
): AisleGroup[] => {
    const layout = aisleLayoutFor(warehouse);
    const currentLocation = currentLine?.location ?? null;
    const currentKey = currentLine ? layout.groupOf(aisleOfLine(currentLine)).key : null;

    const groups = new Map<string, GroupSlot>();
    for (const line of lines) {
        if (!OPEN_STATUSES.has(line.status)) continue;
        const remaining = remainingOf(line);
        if (remaining <= 0) continue;
        const group = layout.groupOf(aisleOfLine(line));
        const walk = walkOf(line);
        let g = groups.get(group.key);
        if (!g) {
            g = { label: group.label, remaining: 0, firstWalk: walk, locs: new Map() };
            groups.set(group.key, g);
        }
        g.remaining += remaining;
        g.firstWalk = Math.min(g.firstWalk, walk);
        const loc = g.locs.get(line.location);
        if (loc) {
            loc.remaining += remaining;
            loc.firstWalk = Math.min(loc.firstWalk, walk);
        } else {
            g.locs.set(line.location, { display: displayOf(line) || line.location, remaining, firstWalk: walk });
        }
    }

    const byWalk = <T extends { firstWalk: number }>(a: [string, T], b: [string, T]) =>
        a[1].firstWalk - b[1].firstWalk || a[0].localeCompare(b[0]);

    return [...groups.entries()]
        .sort(byWalk)
        .map(([key, g]) => ({
            key,
            label: g.label,
            remaining: g.remaining,
            current: key === currentKey,
            locations: [...g.locs.entries()]
                .sort(byWalk)
                .map(([location, l]) => ({
                    location,
                    display: l.display,
                    remaining: l.remaining,
                    current: location === currentLocation,
                })),
        }));
};
